import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { questionsAPI } from '../services/apiService';
import { useSEO } from '../hooks/useSEO';
import { initializeTracking, trackPasswordVerification } from '../utils/trackingUtils';

const MBTITest = () => {
  // Use SEO hook for MBTI Test page
  const { seoData } = useSEO('mbti', {
    h1: 'Test MBTI - Kiểm tra tính cách 16 nhóm',
    metaDescription: 'Làm bài test MBTI miễn phí để khám phá tính cách của bạn thuộc 1 trong 16 nhóm tính cách. Kết quả chi tiết và chính xác.',
    title: 'Test MBTI Online - Kiểm tra tính cách 16 nhóm'
  });
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState('intro'); // intro, test, completed, password, result
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [timeLeft, setTimeLeft] = useState(1800); // 30 minutes in seconds
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [mbtiResult, setMbtiResult] = useState('');
  const [mbtiDetails, setMbtiDetails] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [reviewLaterQuestions, setReviewLaterQuestions] = useState([]);
  const [isAutoAdvancing, setIsAutoAdvancing] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showNeedAnswerNotification, setShowNeedAnswerNotification] = useState(false);
  const [isReviewingMode, setIsReviewingMode] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Load mật khẩu từ localStorage hoặc sử dụng mặc định
  const [correctPassword, setCorrectPassword] = useState('mbti2025');
  
  // Load password from localStorage
  useEffect(() => {
    const savedTestPasswords = localStorage.getItem('test_passwords');
    if (savedTestPasswords) {
      const passwords = JSON.parse(savedTestPasswords);
      setCorrectPassword(passwords.mbti || 'mbti2025');
    }
  }, []);

  // Load questions from API database
  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setIsLoading(true);
        console.log('🔍 Loading MBTI questions from API...');
        
        const apiQuestions = await questionsAPI.getByType('mbti');
        console.log(`✅ Raw API response:`, apiQuestions);
        
        if (apiQuestions && Array.isArray(apiQuestions) && apiQuestions.length > 0) {
          // Transform API data to match expected format
          const transformedQuestions = apiQuestions.map((q, index) => {
            console.log('🔍 Processing question:', q.question);
            console.log('🔍 Raw options:', q.options);
            
            // Validate and transform options
            let transformedOptions = [];
            
            if (Array.isArray(q.options) && q.options.length > 0) {
              transformedOptions = q.options.map((opt, optIndex) => {
                const transformed = {
                  id: opt.id || optIndex,
                  text: opt.option_text || opt.text || `Option ${optIndex + 1}`,
                  value: opt.option_value || opt.value || `${optIndex}`
                };
                console.log('🔄 Transformed option:', transformed);
                return transformed;
              });
            } else {
              // Fallback options if no options available
              transformedOptions = [
                { id: 0, text: 'A', value: 'A' },
                { id: 1, text: 'B', value: 'B' }
              ];
              console.warn('⚠️ No options found, using fallback');
            }
            
            return {
              id: q.id || index + 1,
              question: q.question || `Question ${index + 1}`,
              options: transformedOptions
            };
          });
          
          console.log('✅ Final transformed questions:', transformedQuestions);
          setQuestions(transformedQuestions);
        } else {
          console.warn('❌ No MBTI questions found in database');
          // Fallback to defaultQuestions only if API fails completely
          try {
            const defaultQuestions = (await import('../questions.js')).default;
            if (Array.isArray(defaultQuestions) && defaultQuestions.length > 0) {
              setQuestions(defaultQuestions);
              console.log(`⚠️ Using fallback: ${defaultQuestions.length} default questions`);
            } else {
              throw new Error('Default questions not available');
            }
          } catch (fallbackError) {
            console.error('❌ Fallback failed:', fallbackError);
            // Final fallback - minimal questions
            setQuestions([
              {
                id: 1,
                question: "Bạn thường quan tâm nhiều hơn đến:",
                options: [
                  { id: 0, text: "Thế giới bên ngoài và người khác", value: "E" },
                  { id: 1, text: "Thế giới bên trong và suy nghĩ riêng", value: "I" }
                ]
              }
            ]);
          }
        }
      } catch (error) {
        console.error('❌ Error loading MBTI questions:', error);
        // Fallback to defaultQuestions only on error
        try {
          const defaultQuestions = (await import('../questions.js')).default;
          if (Array.isArray(defaultQuestions) && defaultQuestions.length > 0) {
            setQuestions(defaultQuestions);
            console.log(`⚠️ Using fallback due to error: ${defaultQuestions.length} default questions`);
          } else {
            throw new Error('Default questions not available');
          }
        } catch (fallbackError) {
          console.error('❌ Even fallback failed:', fallbackError);
          // Final fallback - minimal questions
          setQuestions([
            {
              id: 1,
              question: "Bạn thường quan tâm nhiều hơn đến:",
              options: [
                { id: 0, text: "Thế giới bên ngoài và người khác", value: "E" },
                { id: 1, text: "Thế giới bên trong và suy nghĩ riêng", value: "I" }
              ]
            }
          ]);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadQuestions();
  }, []);

  // Initialize tracking when component mounts
  useEffect(() => {
    initializeTracking('mbti');
  }, []);

  // Timer countdown
  useEffect(() => {
    if (currentScreen === 'test' && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && currentScreen === 'test') {
      calculateResult();
    }
  }, [timeLeft, currentScreen]);

  // Auto-hide notification after 5 seconds
  useEffect(() => {
    if (showNeedAnswerNotification) {
      const timer = setTimeout(() => {
        setShowNeedAnswerNotification(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showNeedAnswerNotification]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const startTest = () => {
    setCurrentScreen('test');
    setCurrentQuestion(0);
    setAnswers([]);
    setTimeLeft(1800);
    setIsReviewingMode(false); // Reset reviewing mode when starting test
    setReviewLaterQuestions([]); // Clear review later questions
    // Scroll to top when starting test
    window.scrollTo(0, 0);
  };

  const handleAnswer = (value) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = value;
    setAnswers(newAnswers);
  };

  const nextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setShowNeedAnswerNotification(false); // Hide notification when moving to next question
      setIsReviewingMode(false); // Exit reviewing mode when navigating manually
    }
  };

  const prevQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
      setShowNeedAnswerNotification(false); // Hide notification when moving to previous question
      setIsReviewingMode(false); // Exit reviewing mode when navigating manually
    }
  };

  const toggleReviewLater = () => {
    const newReviewLater = [...reviewLaterQuestions];
    const wasMarkedForReview = newReviewLater.includes(currentQuestion);
    
    if (wasMarkedForReview) {
      // Remove from review list (bỏ đánh dấu)
      newReviewLater.splice(newReviewLater.indexOf(currentQuestion), 1);
    } else {
      // Add to review list (đánh dấu)
      newReviewLater.push(currentQuestion);
    }
    setReviewLaterQuestions(newReviewLater);
    
    // Handle reviewing mode logic
    if (isReviewingMode && wasMarkedForReview) {
      // If we're in reviewing mode and user clicked "Bỏ đánh dấu"
      // Navigate to next purple question (same as when selecting answer)
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (newReviewLater.length > 0) {
          // Go to next purple question
          const nextReviewQuestion = findNextReviewQuestion(currentQuestion, newReviewLater);
          setCurrentQuestion(nextReviewQuestion);
        } else {
          // No more review questions, go to last question and exit review mode
          setCurrentQuestion(questions.length - 1);
          setIsReviewingMode(false);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    } else {
      // Normal mode or marking for review - show notification and stay at current question
      setShowNeedAnswerNotification(true);
      
      // Exit reviewing mode when user marks question for review
      setIsReviewingMode(false);
    }
  };

  const goToQuestion = (index) => {
    setCurrentQuestion(index);
    setShowNeedAnswerNotification(false); // Hide notification when jumping to specific question
    setIsReviewingMode(false); // Exit reviewing mode when jumping to specific question
  };

  // Helper function to find the next purple question (review later)
  const findNextReviewQuestion = (currentIndex, reviewList) => {
    // Find the next review question after currentIndex
    const nextReviewQuestions = reviewList.filter(q => q > currentIndex);
    if (nextReviewQuestions.length > 0) {
      return Math.min(...nextReviewQuestions);
    }
    
    // If no review questions after current, find the first one overall
    const allReviewQuestions = reviewList.filter(q => q !== currentIndex);
    if (allReviewQuestions.length > 0) {
      return Math.min(...allReviewQuestions);
    }
    
    // If no more review questions, return last question
    return questions.length - 1;
  };

  const finishTest = () => {
    const answeredCount = answers.filter(a => a !== undefined && a !== null).length;
    if (answeredCount < questions.length) {
      setShowIncompleteModal(true);
      return;
    }
    
    // Check if there are questions marked for review
    if (reviewLaterQuestions.length > 0) {
      setShowReviewModal(true);
      return;
    }
    
    calculateResult();
  };

  const handleForceSubmit = () => {
    setShowReviewModal(false);
    setIsReviewingMode(false); // Exit reviewing mode when force submit
    calculateResult();
  };

  const handleGoToReview = () => {
    setShowReviewModal(false);
    setIsReviewingMode(true); // Enable reviewing mode
    // Go to the first review question (smallest index)
    const firstReviewQuestion = Math.min(...reviewLaterQuestions);
    setCurrentQuestion(firstReviewQuestion);
  };

  const selectAnswer = (value) => {
    const newAnswers = [...answers];
    newAnswers[currentQuestion] = value;
    setAnswers(newAnswers);
    
    // Hide notification if it was showing
    setShowNeedAnswerNotification(false);
    
    // Handle reviewing mode logic
    if (isReviewingMode) {
      // Remove current question from review list (turn green)
      const newReviewLater = reviewLaterQuestions.filter(q => q !== currentQuestion);
      setReviewLaterQuestions(newReviewLater);
      
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (newReviewLater.length > 0) {
          // Go to next purple question
          const nextReviewQuestion = findNextReviewQuestion(currentQuestion, newReviewLater);
          setCurrentQuestion(nextReviewQuestion);
        } else {
          // No more review questions, go to last question and exit review mode
          setCurrentQuestion(questions.length - 1);
          setIsReviewingMode(false);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    } else {
      // Normal mode - Auto-advance logic - chỉ đi sequential, không đi đến review questions
      setIsAutoAdvancing(true);
      setTimeout(() => {
        if (currentQuestion < questions.length - 1) {
          // Go to next sequential question
          setCurrentQuestion(currentQuestion + 1);
        } else {
          // Go to last question for submit
          setCurrentQuestion(questions.length - 1);
        }
        
        setIsAutoAdvancing(false);
      }, 200);
    }
  };

  const calculateResult = () => {
    const typeCount = { E: 0, I: 0, S: 0, N: 0, T: 0, F: 0, J: 0, P: 0 };
    
    answers.forEach(answer => {
      if (answer) typeCount[answer]++;
    });

    const mbtiType = 
      (typeCount.E > typeCount.I ? 'E' : 'I') +
      (typeCount.S > typeCount.N ? 'S' : 'N') +
      (typeCount.T > typeCount.F ? 'T' : 'F') +
      (typeCount.J > typeCount.P ? 'J' : 'P');

    const detailsString = `Phân tích chi tiết:
• Hướng năng lượng: ${typeCount.E > typeCount.I ? 'Hướng ngoại (E)' : 'Hướng nội (I)'}
• Thu thập thông tin: ${typeCount.S > typeCount.N ? 'Giác quan (S)' : 'Trực giác (N)'}
• Ra quyết định: ${typeCount.T > typeCount.F ? 'Lý trí (T)' : 'Cảm xúc (F)'}
• Lối sống: ${typeCount.J > typeCount.P ? 'Nguyên tắc (J)' : 'Linh hoạt (P)'}`;

    // Navigate to test result page with data
    navigate('/test-result', {
      state: {
        testType: 'mbti',
        result: mbtiType,
        details: getMBTIDescription(mbtiType),
        breakdown: detailsString
      }
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (password === correctPassword) {
      setCurrentScreen('result');
      setPasswordError('');

      // Track password verification
      await trackPasswordVerification('mbti');
    } else {
      setPasswordError('Mật khẩu không chính xác. Vui lòng thử lại!');
    }
  };

  const getMBTIDescription = (type) => {
    const descriptions = {
      'INTJ': 'Nhà Kiến Tạo - Độc lập, quyết đoán, có tầm nhìn xa',
      'INTP': 'Nhà Tư Duy - Logic, sáng tạo, thích phân tích',
      'ENTJ': 'Nhà Chỉ Huy - Mạnh mẽ, tự tin, là nhà lãnh đạo bẩm sinh',
      'ENTP': 'Nhà Tranh Luận - Nhanh nhẹn, thông minh, thích thử thách',
      'INFJ': 'Nhà Bảo Vệ - Sâu sắc, có cảm nhận tốt, quan tâm người khác',
      'INFP': 'Nhà Lý Tưởng - Trung thành, sáng tạo, theo đuổi giá trị',
      'ENFJ': 'Nhà Lãnh Đạo - Ấm áp, có sức ảnh hưởng, truyền cảm hứng',
      'ENFP': 'Nhà Truyền Cảm Hứng - Nhiệt tình, sáng tạo, xã hội',
      'ISTJ': 'Người Trách Nhiệm - Cẩn thận, đáng tin cậy, thực tế',
      'ISFJ': 'Người Bảo Vệ - Tận tâm, ấm áp, có trách nhiệm',
      'ESTJ': 'Nhà Quản Lý - Thực tế, quyết đoán, có tổ chức',
      'ESFJ': 'Người Chăm Sóc - Hòa đồng, nhiệt tình, hợp tác',
      'ISTP': 'Thợ Thủ Công - Linh hoạt, thực tế, thích hành động',
      'ISFP': 'Nghệ Sĩ - Nhạy cảm, tốt bụng, thích khám phá',
      'ESTP': 'Nhà Kinh Doanh - Năng động, thực tế, thích trải nghiệm',
      'ESFP': 'Người Biểu Diễn - Vui vẻ, nhiệt tình, thích tự do'
    };
    return descriptions[type] || 'Hãy khám phá thêm về tính cách của bạn!';
  };

  const getDetailDescription = (dimension, value) => {
    const descriptions = {
      'E': 'Hướng ngoại',
      'I': 'Hướng nội',
      'S': 'Giác quan',
      'N': 'Trực giác',
      'T': 'Lý trí',
      'F': 'Cảm xúc',
      'J': 'Nguyên tắc',
      'P': 'Linh hoạt'
    };
    return descriptions[value] || value;
  };

  const getQuestionStatus = (index) => {
    if (reviewLaterQuestions.includes(index)) return 'review';
    if (answers[index] !== undefined && answers[index] !== null) return 'answered';
    return 'unanswered';
  };

  const getQuestionStatusColor = (status) => {
    switch (status) {
      case 'answered': return 'bg-green-500 text-white';
      case 'review': return 'bg-purple-500 text-white';
      default: return 'bg-white border-2 border-gray-300 text-gray-700';
    }
  };

  // Modern question board component
  const ModernQuestionBoard = () => (
    <div className="bg-white rounded-xl shadow-lg px-5 py-5 sticky top-8">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-800 text-center">Bảng theo dõi</h3>
      </div>
      
      <div className="grid grid-cols-7 gap-2 mb-5">
        {questions.map((_, index) => (
          <button
            key={index}
            onClick={() => goToQuestion(index)}
            className={`w-8 h-8 rounded-full text-xs font-semibold transition-all duration-200 hover:scale-105 ${
              index === currentQuestion 
                ? 'ring-2 ring-blue-400 ring-offset-1 ' + getQuestionStatusColor(getQuestionStatus(index))
                : getQuestionStatusColor(getQuestionStatus(index))
            }`}
          >
            {index + 1}
          </button>
        ))}
      </div>
      
      <button
        onClick={finishTest}
        className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2.5 px-3 rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg text-sm mb-5"
      >
        NỘP BÀI
      </button>
      
      <div className="grid grid-cols-3 gap-1 text-xs">
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-green-500 rounded-full mb-1"></div>
          <span className="text-gray-600">Đã làm</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-gray-300 rounded-full mb-1"></div>
          <span className="text-gray-600">Chưa làm</span>
        </div>
        <div className="flex flex-col items-center">
          <div className="w-2.5 h-2.5 bg-purple-500 rounded-full mb-1"></div>
          <span className="text-gray-600">Cần kiểm tra</span>
        </div>
      </div>
    </div>
  );

  // Intro Screen
  if (currentScreen === 'intro') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 relative">
        {/* Subtle background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-blue-100 rounded-full opacity-30 blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-80 h-80 bg-purple-100 rounded-full opacity-20 blur-3xl"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto text-center">
            {/* Header */}
            <div className="mb-8">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-sm font-medium rounded-full mb-6">
                ✨ Bài test tính cách chính thức
              </span>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Test Tính Cách <span className="text-blue-600">MBTI</span>
              </h1>
              
              <p className="text-lg text-gray-600 mb-12 max-w-2xl mx-auto">
                Khám phá tính cách và tiềm năng của bạn với bài test MBTI chuẩn quốc tế
              </p>
            </div>

            {/* Main card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 border border-gray-100">
              <h2 className="text-2xl font-semibold text-gray-800 mb-8">Thông tin bài Test MBTI chuẩn Quốc Tế</h2>

              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {isLoading ? '...' : questions.length}
                  </div>
                  <div className="text-blue-700 font-medium">Câu hỏi</div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-purple-600 mb-2">30</div>
                  <div className="text-purple-700 font-medium">Phút</div>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 text-center">
                  <div className="text-3xl font-bold text-green-600 mb-2">16</div>
                  <div className="text-green-700 font-medium">Loại tính cách</div>
                </div>
              </div>

              {/* Note about full version */}
              <div className="flex justify-center mb-6">
                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 inline-flex items-center gap-2 text-sm text-blue-700">
                  <svg className="h-4 w-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span>
                    Bản đầy đủ <strong>{isLoading ? '' : questions.length + ' câu'}</strong> • Độ chính xác cao • Miễn phí
                  </span>
                </div>
              </div>

              {/* CTA Button */}
              <button
                onClick={startTest}
                className="group relative bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-bold text-lg py-4 px-12 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 mx-auto"
              >
                <span className="flex items-center justify-center">
                  Bắt đầu làm bài ngay
                </span>
              </button>
              
              <p className="text-sm text-gray-500 mt-4">
                Miễn phí • Không cần đăng ký • Kết quả tức thì
              </p>
            </div>

            {/* Guidelines */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-left">
              <div className="flex items-center mb-8">
                <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center mr-4">
                  <span className="text-2xl">💡</span>
                </div>
                <h3 className="text-2xl font-semibold text-gray-800">Lưu ý quan trọng</h3>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h4 className="font-semibold text-blue-700 mb-3">Trạng thái thoải mái</h4>
                  <p className="text-gray-600 text-sm">
                    Làm bài khi tâm trạng ổn định, không bị căng thẳng hay áp lực
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h4 className="font-semibold text-green-700 mb-3">Trả lời thật lòng</h4>
                  <p className="text-gray-600 text-sm">
                    Chọn đáp án phản ánh đúng cách bạn hành xử trong thực tế
                  </p>
                </div>
                
                <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-4 mx-auto">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h4 className="font-semibold text-purple-700 mb-3">Không bị ảnh hưởng</h4>
                  <p className="text-gray-600 text-sm">
                    Tránh để ý kiến người khác tác động đến câu trả lời của bạn
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Test Screen
  if (currentScreen === 'test') {
    // Don't render if questions not loaded yet
    if (isLoading || questions.length === 0) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-lg text-gray-600">🔄 Đang tải câu hỏi từ database...</p>
            <p className="mt-2 text-sm text-gray-500">Vui lòng chờ trong giây lát</p>
          </div>
        </div>
      );
    }

    const answeredCount = answers.filter(a => a !== undefined && a !== null).length;
    const progress = (answeredCount / questions.length) * 100;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50 py-8">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="xl:col-span-2">
                {/* Header */}
                <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center space-x-3">
                      <h2 className="text-xl md:text-2xl font-bold text-gray-800">Test MBTI chuẩn Myers-Briggs</h2>
                      {isReviewingMode && (
                        <div className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm font-medium animate-pulse">
                          🔍 Đang kiểm tra lại
                        </div>
                      )}
                    </div>
                    <div className="bg-red-100 text-red-600 px-4 py-2 rounded-full text-base font-bold">
                      ⏱️ {formatTime(timeLeft)}
                    </div>
                  </div>
                  
                  {/* Progress Bar */}
                  <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                    <div 
                      className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-sm text-gray-600">
                    <span>Tiến độ: {answeredCount} / {questions.length}</span>
                    <span>Câu hiện tại: {currentQuestion + 1}</span>
                  </div>
                </div>

                {/* Mobile Progress Grid */}
                <div className="bg-white rounded-lg shadow-lg p-4 mb-6 xl:hidden">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-800 text-sm">Bảng theo dõi</h3>
                    <button
                      onClick={finishTest}
                      className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-bold py-2 px-4 rounded-lg text-sm"
                    >
                      NỘP BÀI
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-10 gap-1 mb-3">
                    {questions.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToQuestion(index)}
                        className={`w-7 h-7 rounded-full text-xs font-medium transition-all duration-200 ${
                          index === currentQuestion 
                            ? 'ring-2 ring-blue-400 ' + getQuestionStatusColor(getQuestionStatus(index))
                            : getQuestionStatusColor(getQuestionStatus(index))
                        }`}
                      >
                        {index + 1}
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-600">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-500 rounded mr-1"></div>
                      <span>Đã làm</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-white border border-gray-300 rounded mr-1"></div>
                      <span>Chưa làm</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-purple-500 rounded mr-1"></div>
                      <span>Cần kiểm tra</span>
                    </div>
                  </div>
                </div>

                {/* Question */}
                <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
                  <div className="flex items-center mb-6">
                    <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-sm font-semibold mr-4">
                      {currentQuestion + 1}
                    </span>
                    <h3 className="text-xl font-semibold text-gray-800">
                      {questions[currentQuestion]?.question || 'Đang tải câu hỏi...'}
                    </h3>
                  </div>
                  
                  <div className="space-y-4">
                    {questions[currentQuestion]?.options?.map((option, index) => (
                      <button
                        key={`${currentQuestion}-${index}-${option.id || index}`}
                        onClick={() => {
                          selectAnswer(option.value);
                          handleAnswer(option.value);
                        }}
                        className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                          answers[currentQuestion] === option.value
                            ? 'border-blue-500 bg-blue-50 text-blue-800 shadow-sm'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center">
                          <div className={`w-8 h-8 rounded-full border-2 mr-4 flex items-center justify-center ${
                            answers[currentQuestion] === option.value
                              ? 'border-blue-500 bg-blue-500'
                              : 'border-gray-300'
                          }`}>
                            <span className={`text-base font-bold ${
                              answers[currentQuestion] === option.value
                                ? 'text-white'
                                : 'text-gray-600'
                            }`}>
                              {String.fromCharCode(65 + index)}
                            </span>
                          </div>
                          <span className="text-gray-800 text-lg">{option.text || 'Đang tải...'}</span>
                        </div>
                      </button>
                    )) || (
                      <div className="text-center text-gray-500 py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        Đang tải options...
                      </div>
                    )}
                  </div>
                  
                  {/* Review Later Button Area - Mobile Responsive */}
                  <div className="mt-8 space-y-4">
                    {/* Review Later Button */}
                    <div className="flex justify-center">
                      <button
                        onClick={toggleReviewLater}
                        className={`px-3 py-2 sm:px-4 sm:py-2 rounded-lg font-medium transition-all duration-200 text-xs sm:text-sm ${
                          reviewLaterQuestions.includes(currentQuestion)
                            ? 'bg-purple-500 text-white hover:bg-purple-600'
                            : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
                        }`}
                      >
                        {reviewLaterQuestions.includes(currentQuestion) ? 'Bỏ đánh dấu' : 'Lát kiểm tra lại'}
                      </button>
                    </div>
                    
                    {/* Answer Required Notification - Mobile Responsive */}
                    {showNeedAnswerNotification && (
                      <div className="flex justify-center px-4">
                        <div className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-2 sm:px-4 sm:py-2 rounded-lg shadow-md border border-blue-700 text-center max-w-full">
                          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-xs sm:text-sm font-medium break-words">Vui lòng chọn trước một đáp án để tiếp tục!</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Navigation - Mobile Responsive */}
                <div className="bg-white rounded-lg shadow-lg p-3 sm:p-4 sticky bottom-4 z-10">
                  <div className="flex justify-between items-center gap-2 sm:gap-4">
                    <button
                      onClick={prevQuestion}
                      disabled={currentQuestion === 0}
                      className="flex items-center px-2 py-2 sm:px-4 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-xs sm:text-sm whitespace-nowrap"
                    >
                      <span className="hidden sm:inline">← Câu trước</span>
                      <span className="sm:hidden">←</span>
                    </button>

                    <div className="flex gap-2 sm:gap-3">
                      {isAutoAdvancing ? (
                        <div className="px-2 py-2 sm:px-4 sm:py-2 bg-blue-100 text-blue-600 rounded-lg text-xs sm:text-sm font-medium flex items-center">
                          <svg className="animate-spin -ml-1 mr-1 sm:mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="hidden sm:inline">Đang chuyển...</span>
                          <span className="sm:hidden">...</span>
                        </div>
                      ) : currentQuestion === questions.length - 1 ? (
                        <button
                          onClick={finishTest}
                          className="px-2 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 font-medium transition-all duration-200 text-xs sm:text-sm whitespace-nowrap"
                        >
                          <span className="hidden sm:inline">Hoàn thành</span>
                          <span className="sm:hidden">✓</span>
                        </button>
                      ) : (
                        <button
                          onClick={nextQuestion}
                          className="px-2 py-2 sm:px-4 sm:py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 font-medium transition-all duration-200 text-xs sm:text-sm whitespace-nowrap"
                        >
                          <span className="hidden sm:inline">Câu tiếp →</span>
                          <span className="sm:hidden">→</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Modern Question Board - Desktop Only */}
              <div className="hidden xl:block">
                <ModernQuestionBoard />
              </div>
            </div>
          </div>
        </div>

        {/* Incomplete Modal */}
        {showIncompleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-orange-100 mb-4">
                  <svg className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Chưa hoàn thành đủ câu hỏi
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn cần hoàn thành tất cả {questions.length} câu hỏi trước khi nộp bài! 
                  Hiện tại bạn đã làm {answers.filter(a => a !== undefined && a !== null).length}/{questions.length} câu.
                </p>
                <button
                  type="button"
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md font-medium hover:bg-blue-700 transition-colors"
                  onClick={() => setShowIncompleteModal(false)}
                >
                  Tiếp tục làm bài
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Review Later Modal */}
        {showReviewModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl p-6 mx-4 max-w-md w-full">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-purple-100 mb-4">
                  <svg className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Còn câu hỏi cần kiểm tra lại
                </h3>
                <p className="text-sm text-gray-500 mb-6">
                  Bạn có {reviewLaterQuestions.length} câu hỏi được đánh dấu cần kiểm tra lại. 
                  Bạn có muốn xem lại những câu này trước khi nộp bài không?
                </p>
                <div className="space-y-3">
                  <button
                    type="button"
                    className="w-full bg-purple-600 text-white px-4 py-2 rounded-md font-medium hover:bg-purple-700 transition-colors"
                    onClick={handleGoToReview}
                  >
                    Kiểm tra lại
                  </button>
                  <button
                    type="button"
                    className="w-full bg-gray-200 text-gray-800 px-4 py-2 rounded-md font-medium hover:bg-gray-300 transition-colors"
                    onClick={handleForceSubmit}
                  >
                    Chấp nhận và nộp bài
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


      </div>
    );
  }

  // Completed Screen - Modal with green check
  if (currentScreen === 'completed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 py-8">
        <div className="max-w-2xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="mb-8">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-4">
                🎉 Chúc mừng! Bạn đã hoàn thành bài test!
              </h1>
              <p className="text-lg text-gray-600">
                Nhập mật khẩu để xem kết quả chi tiết
              </p>
            </div>

            <form onSubmit={handlePasswordSubmit} className="mb-8">
              <div className="relative mb-4">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-blue-500 focus:outline-none"
                  placeholder="Nhập mật khẩu để xem kết quả"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-500"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {passwordError && <p className="text-red-500 text-sm mb-4">{passwordError}</p>}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold py-3 px-6 rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200"
              >
                Xem kết quả
              </button>
            </form>

            <button
              onClick={() => navigate('/')}
              className="text-blue-600 hover:text-blue-800 font-medium"
            >
              ← Quay về trang chủ
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Result Screen
  if (currentScreen === 'result') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-8 text-center">
              <h2 className="text-3xl font-bold text-gray-800 mb-6">
                Kết Quả MBTI Của Bạn
              </h2>
              
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg p-8 mb-8 text-white">
                <div className="text-5xl font-bold mb-4">
                  {mbtiResult}
                </div>
                <div className="text-xl">
                  {getMBTIDescription(mbtiResult)}
                </div>
              </div>
              
              <div className="mb-8">
                <h3 className="text-2xl font-semibold mb-6 text-gray-800">Chi tiết kết quả:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-blue-50 rounded-lg p-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Hướng ngoại/Hướng nội:</span>
                      <span className="font-bold text-blue-600">{mbtiDetails.EI}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {getDetailDescription('EI', mbtiDetails.EI)}
                    </div>
                  </div>
                  
                  <div className="bg-green-50 rounded-lg p-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Giác quan/Trực giác:</span>
                      <span className="font-bold text-green-600">{mbtiDetails.SN}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {getDetailDescription('SN', mbtiDetails.SN)}
                    </div>
                  </div>
                  
                  <div className="bg-purple-50 rounded-lg p-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Lý trí/Cảm xúc:</span>
                      <span className="font-bold text-purple-600">{mbtiDetails.TF}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {getDetailDescription('TF', mbtiDetails.TF)}
                    </div>
                  </div>
                  
                  <div className="bg-yellow-50 rounded-lg p-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-600">Nguyên tắc/Linh hoạt:</span>
                      <span className="font-bold text-yellow-600">{mbtiDetails.JP}</span>
                    </div>
                    <div className="text-sm text-gray-500">
                      {getDetailDescription('JP', mbtiDetails.JP)}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <button
                  onClick={() => {
                    setCurrentScreen('intro');
                    setCurrentQuestion(0);
                    setAnswers([]);
                    setPassword('');
                    setPasswordError('');
                  }}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 transform hover:scale-105"
                >
                  Làm lại bài test
                </button>
                
                <button
                  onClick={() => navigate('/')}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-3 px-6 rounded-lg transition-colors duration-200"
                >
                  Quay lại trang chủ
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
};

export default MBTITest; 